import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG, loadAppConfig } from '../../config/apiConfig'
import { showToast, showConfirm } from "../../utils/messageUtil"
import { fileSvc } from '../../utils/FileSvc'
import { useUser } from '../../contexts/UserContext'
import { parseLocalDateTime } from '../../utils/dateUtil'
import './index.css'

const getStatusText = (status) => {
    switch (status) {
        case 0:
            return '待车管审批'
        case 1:
            return '待还车'
        case 2:
            return '用车审批驳回'
        case 3:
            return '还车待确认'
        case 4:
            return '已还车'
        case 5:
            return '还车不规范'
        case 6:
            return '取消用车'
        case 7:
            return '待上级审批'
        case 8:
            return '已撤销申请'
        case 9:
            return '上级审批驳回'
        default:
            return '未知状态'
    }
}

const getStatusClass = (status) => {
    switch (status) {
        case 0:
        case 3:
        case 7:
            return 'status-pending'
        case 1:
            return 'status-approved'
        case 2:
        case 5:
        case 9:
            return 'status-cancelled'
        case 4:
        case 6:
        case 8:
            return 'status-completed'
        default:
            return ''
    }
}

export default function CarReport() {

    const navigate = useNavigate()
    const { userInfo, isAdmin, isDriver, loading: userLoading, refreshUserInfo } = useUser()
    const [hasRefreshed, setHasRefreshed] = useState(false)

    // 检查用户信息是否完整
    const isUserInfoComplete = () => {
        if (!userInfo) return false
        const hasUserId = userInfo.open_id || userInfo.user_id
        // 用车报表页面只需要user_id
        return hasUserId !== ''
    }

    // 如果用户信息缺失，重新获取
    useEffect(() => {
        // 只有在加载完成后、用户信息不完整且还未刷新过时才重新获取
        if (!userLoading && !isUserInfoComplete() && !hasRefreshed) {
            console.log('用车报表页面：用户信息不完整，重新获取...')
            setHasRefreshed(true)
            refreshUserInfo()
        }
    }, [userLoading, userInfo, refreshUserInfo, hasRefreshed])

    const [reports, setReports] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    // 筛选条件
    const [filters, setFilters] = useState({
        keyword: '',
        applyStartTime: '',
        applyEndTime: ''
    })
    const [showOverdue, setShowOverdue] = useState(false)
    // 弹窗状态
    const [detailModalVisible, setDetailModalVisible] = useState(false)
    const [selectedReport, setSelectedReport] = useState(null)
    const [returningStates, setReturningStates] = useState({})
    const [cancelingStates, setCancelingStates] = useState({})
    const [urgingStates, setUrgingStates] = useState({})
    const [changeStates, setChangeStates] = useState({})
    const [cityList, setCityList] = useState([])
    const [selectedCity, setSelectedCity] = useState('')
    // 城市下拉数据源：从已加载的分页数据中累积提取（只增不减，不依赖外部接口）
    const cityAccumulatorRef = useRef(new Set())

    // 改派弹窗状态
    const [reassignModalVisible, setReassignModalVisible] = useState(false)
    const [reassignForm, setReassignForm] = useState({
        expectedStartTime: '',
        expectedEndTime: '',
        plateNumber: '',
        vehicleId: ''
    })
    const [availableVehicles, setAvailableVehicles] = useState([])
    const [loadingVehicles, setLoadingVehicles] = useState(false)

    const containerRef = useRef(null)

    // 获取用车报表数据
    const fetchReports = async (page = 1) => {
        try {
            if (page === 1) {
                setLoading(true)
            } else {
                setLoadingMore(true)
            }
            setError(null)

            // 将日期格式转换为后端期望的 yyyy-MM-dd HH:mm:ss 格式
            const formatDateTime = (dateStr, isEnd = false) => {
                if (!dateStr) return null;
                return isEnd ? `${dateStr} 23:59:59` : `${dateStr} 00:00:00`;
            };

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getCarReportList`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                page: page,
                rows: 10,
                keyword: filters.keyword,
                applyStartTime: formatDateTime(filters.applyStartTime),
                applyEndTime: formatDateTime(filters.applyEndTime, true),
                // 超期未还车筛选：传给后端在 SQL 层过滤（后端 Mapper 已支持 showOverdue），
                // 保证分页总数与翻页结果都基于过滤后的数据，不再前端只过滤当前页
                showOverdue: showOverdue,
                // 城市归属地筛选：同样下沉到后端 SQL 过滤（后端 Mapper 已支持 cityName），
                // 保证分页/翻页/导出都只返回该城市的数据
                cityName: selectedCity || null
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.success) {
                const reportData = response.data.result.rows || []
                const total = response.data.result.total || 0
                const records = response.data.result.records || 0
                const rowsPerPage = 10
                const calculatedTotalPages = Math.ceil(records / rowsPerPage)

                setTotalPages(calculatedTotalPages)
                setCurrentPage(page)
                setHasMore(page < calculatedTotalPages)

                if (page === 1) {
                    setReports(reportData)
                } else {
                    setReports(prevReports => [...prevReports, ...reportData])
                }
            } else {
                if (page === 1) {
                    setReports([])
                }
                setError('获取报表数据失败')
            }
        } catch (error) {
            console.error('获取报表数据失败:', error)
            if (page === 1) {
                setReports([])
            }
            setError('网络错误，请稍后重试')
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }

    // 查询数据（filters/showOverdue/selectedCity 任一变化时重新查询第1页）
    useEffect(() => {
        (async () => {
            await loadAppConfig();
            fetchCities();
            fetchReports(1);
        })();
    }, [filters, showOverdue, selectedCity])

    // 独立接口获取该租户全量城市列表（走 /api/v1/carApplication/listReportCities，Node 已代理）
    // 并入下拉累积（只增不减）；仅首次调用（citiesFetchedRef 防重复，筛选变化不重复请求）
    const citiesFetchedRef = useRef(false)
    const fetchCities = async () => {
        if (citiesFetchedRef.current) return
        try {
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/listReportCities`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            if (response.data && response.data.code === 200 && Array.isArray(response.data.data)) {
                response.data.data.forEach(c => { if (c) cityAccumulatorRef.current.add(c) })
                setCityList([...cityAccumulatorRef.current])
                citiesFetchedRef.current = true
            } else {
                console.warn('获取报表城市列表失败，降级为分页累积:', response.data?.message || '未知错误')
            }
        } catch (error) {
            console.error('获取报表城市列表异常，降级为分页累积:', error)
        }
    }

    // 城市下拉：从已加载的分页数据中累积提取归属城市（只增不减）
    // 配合独立接口的全量城市，保证下拉一开始就完整、筛选后不塌缩
    useEffect(() => {
        if (reports?.length) {
            reports.forEach(i => {
                if (i.cityName) cityAccumulatorRef.current.add(i.cityName)
            })
            setCityList([...cityAccumulatorRef.current])
        }
    }, [reports])

    // 加载更多（依赖含筛选条件，确保翻页时使用最新的城市/超期筛选条件）
    const handleLoadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            const nextPage = currentPage + 1
            fetchReports(nextPage)
        }
    }, [loadingMore, hasMore, currentPage, filters, selectedCity, showOverdue])

    // 上划到底部加载更多
    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current
            if (!container || loadingMore || !hasMore) return

            const { scrollTop, scrollHeight, clientHeight } = container
            // 当滚动到底部附近时触发加载更多
            if (scrollHeight - scrollTop - clientHeight < 50) {
                handleLoadMore()
            }
        }

        const container = containerRef.current
        if (container) {
            container.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll)
            }
        }
    }, [loadingMore, hasMore, handleLoadMore])

    // 处理筛选条件变化
    const handleFilterChange = (e) => {
        const { name, value } = e.target
        setFilters(prev => ({
            ...prev,
            [name]: value
        }))
    }

    // 处理时间范围变化
    const handleTimeRangeChange = (type, value) => {
        setFilters(prev => ({
            ...prev,
            [type]: value
        }))
    }

    // 进入详情（打开弹窗）
    const handleReportDetail = (report) => {
        setSelectedReport(report)
        setDetailModalVisible(true)
    }

    // 取消用车
    const cancelCarApplication = async (applicationId) => {
        // 防重复点击
        if (cancelingStates[applicationId]) return;

        try {
            // 显示确认对话框
            const confirmed = await showConfirm('提示', '取消用车将收回该车辆控车权限，是否确认取消？');
            if (!confirmed) {
                return;
            }

            // 设置当前记录的取消状态为true
            setCancelingStates(prev => {
                const newState = { ...prev };
                newState[applicationId] = true;
                return newState;
            })
            setError(null)

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/cancelCarApplication`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicationId: applicationId
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                // 操作成功，刷新报表数据
                fetchReports(1)
                showToast('取消用车成功', 'success')
            } else {
                showToast('取消用车失败:' + response.data.message, 'error')
            }
        } catch (error) {
            console.error('取消用车失败:', error)
            setError('网络错误，请稍后重试')
        } finally {
            // 设置当前记录的取消状态为false
            setCancelingStates(prev => {
                const newState = { ...prev };
                newState[applicationId] = false;
                return newState;
            })
        }
    }

    // 催还车
    const urgeReturnCar = async (applicationId) => {
        // 防重复点击
        if (urgingStates[applicationId]) return;

        try {
            // 显示确认对话框
            const confirmed = await showConfirm('提示', '确定要催还该车辆吗？');
            if (!confirmed) {
                return;
            }

            // 设置当前记录的催还状态为true
            setUrgingStates(prev => ({
                ...prev,
                [applicationId]: true
            }))
            setError(null)

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/return/urgeReturnCar`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicationId: applicationId
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                // 操作成功，刷新报表数据
                fetchReports(1)
                showToast('已发送催还信息给申请人！', 'success')
            } else {
                showToast('发送催还消息失败:' + response.data.message, 'error')
            }
        } catch (error) {
            console.error('发送催还消息失败:', error)
            setError('网络错误，请稍后重试')
        } finally {
            // 设置当前记录的催还状态为false
            setUrgingStates(prev => ({
                ...prev,
                [applicationId]: false
            }))
        }
    }

    // 代人还车
    const returnCar = async (applicationId) => {
        // 防重复点击
        if (returningStates[applicationId]) return;

        try {
            // 显示确认对话框
            const confirmed = await showConfirm('提示', '该操作将收回用户对该车辆的控车权限并直接还车，是否确认还车？');
            if (!confirmed) {
                return;
            }

            // 设置当前记录的还车状态为true
            setReturningStates(prev => ({
                ...prev,
                [applicationId]: true
            }))
            setError(null)

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/return/returnCar`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicationId: applicationId,
                returnApproverId: userInfo?.open_id || '',
                returnApproverName: userInfo?.name || '',
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                // 操作成功，刷新报表数据
                fetchReports(1)
                showToast('还车操作成功', 'success')
            } else {
                showToast('还车操作失败:' + response.data.message, 'error')
            }
        } catch (error) {
            console.error('还车操作失败:', error)
            setError('网络错误，请稍后重试')
        } finally {
            // 设置当前记录的取消状态为false
            setReturningStates(prev => ({
                ...prev,
                [applicationId]: false
            }))
        }
    }

    // 关闭弹窗
    const handleCloseModal = () => {
        setDetailModalVisible(false)
        setSelectedReport(null)
    }

    // 图片预览状态
    const [previewImage, setPreviewImage] = useState('')

    // 打开图片预览
    const openImagePreview = (imageUrl) => {
        setPreviewImage(imageUrl)
    }

    // 关闭图片预览
    const closeImagePreview = () => {
        setPreviewImage('')
    }

    // 打开改派弹窗
    const changeCar = (report) => {
        setSelectedReport(report)

        // 格式化原申请单的时间为datetime-local格式
        const formatTimeForInput = (timeStr) => {
            // 假设时间格式为 'YYYY-MM-DD HH:mm:ss'
            if (!timeStr) return ''
            const [date, time] = timeStr.split(' ')
            return `${date}T${time.slice(0, 5)}`
        }

        // 初始化改派表单数据
        const formattedStartTime = formatTimeForInput(report.expectedStartTime)
        const formattedEndTime = formatTimeForInput(report.expectedEndTime)

        setReassignForm({
            expectedStartTime: formattedStartTime,
            expectedEndTime: formattedEndTime,
            plateNumber: '',
            vehicleId: ''
        })
        setAvailableVehicles([])
        setReassignModalVisible(true)

        // 直接使用格式化后的时间调用接口获取可预约车辆
        if (formattedStartTime && formattedEndTime) {
            // 转换时间格式为 yyyy-MM-dd HH:mm:ss
            const formatTime = (timeStr) => {
                if (!timeStr) return ''
                return timeStr.replace('T', ' ') + ':00'
            }

            setLoadingVehicles(true)
            axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getVehicleTime`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                startTime: formatTime(formattedStartTime),
                endTime: formatTime(formattedEndTime)
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => {
                    if (response.data && response.data.code === 200) {
                        setAvailableVehicles(response.data.data || [])
                    } else {
                        setAvailableVehicles([])
                    }
                })
                .catch(error => {
                    console.error('获取可预约车辆失败:', error)
                    setAvailableVehicles([])
                })
                .finally(() => {
                    setLoadingVehicles(false)
                })
        }
    }

    // 关闭改派弹窗
    const handleCloseReassignModal = () => {
        setReassignModalVisible(false)
        setReassignForm({
            expectedStartTime: '',
            expectedEndTime: '',
            plateNumber: '',
            vehicleId: ''
        })
        setAvailableVehicles([])
    }

    // 处理改派表单变化
    const handleReassignFormChange = (e) => {
        const { name, value } = e.target
        setReassignForm(prev => ({
            ...prev,
            [name]: value
        }))
    }

    // 获取可预约的车辆
    const fetchAvailableVehicles = async () => {
        const { expectedStartTime, expectedEndTime } = reassignForm

        if (!expectedStartTime || !expectedEndTime) {
            setAvailableVehicles([])
            return
        }

        try {
            setLoadingVehicles(true)
            // 转换时间格式为 yyyy-MM-dd HH:mm:ss
            const formatTime = (timeStr) => {
                if (!timeStr) return ''
                return timeStr.replace('T', ' ') + ':00'
            }

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getVehicleTime`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                startTime: formatTime(expectedStartTime),
                endTime: formatTime(expectedEndTime)
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                setAvailableVehicles(response.data.data || [])
            } else {
                setAvailableVehicles([])
            }
        } catch (error) {
            console.error('获取可预约车辆失败:', error)
            setAvailableVehicles([])
        } finally {
            setLoadingVehicles(false)
        }
    }

    // 处理车牌号选择变化
    const handlePlateNumberChange = (e) => {
        const selectedPlateNumber = e.target.value
        const selectedVehicle = availableVehicles.find(v => v.plateNumber === selectedPlateNumber)

        setReassignForm(prev => ({
            ...prev,
            plateNumber: selectedPlateNumber,
            vehicleId: selectedVehicle ? selectedVehicle.vehicleId : ''
        }))
    }

    // 提交改派审批
    const submitReassign = async () => {
        const { expectedStartTime, expectedEndTime, plateNumber } = reassignForm

        // 校验开始时间必填
        if (!expectedStartTime) {
            showToast('请选择开始时间', 'error')
            return
        }

        // 校验结束时间必填
        if (!expectedEndTime) {
            showToast('请选择结束时间', 'error')
            return
        }

        // 校验车牌号必填
        if (!plateNumber) {
            showToast('请选择车牌号', 'error')
            return
        }

        // 校验结束时间不能早于开始时间
        if (parseLocalDateTime(expectedEndTime) <= parseLocalDateTime(expectedStartTime)) {
            showToast('结束时间不能早于开始时间', 'error')
            return
        }

        // 校验开始时间不能是过去时间
        const startDate = parseLocalDateTime(expectedStartTime)
        if (startDate && startDate < new Date()) {
            showToast('开始时间不能早于当前时间', 'error')
            return
        }

        // 防重复点击
        if (changeStates[selectedReport.applicationId]) return;

        try {
            // 设置当前记录的改派状态为true
            setChangeStates(prev => ({
                ...prev,
                [selectedReport.applicationId]: true
            }))

            // 转换时间格式为 yyyy-MM-dd HH:mm:ss
            const formatTime = (timeStr) => {
                if (!timeStr) return ''
                return timeStr.replace('T', ' ') + ':00'
            }

            const formattedStartTime = formatTime(expectedStartTime)
            const formattedEndTime = formatTime(expectedEndTime)

            // 构建请求参数
            const params = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicationId: selectedReport.applicationId,
                approvalStatus: '1', // 审核通过
                approvalOpinion: '改派车辆',
                approverId: userInfo.open_id || '',
                approverName: userInfo.name || '',
                plateNumber: plateNumber,
                expectedStartTime: formattedStartTime,
                expectedEndTime: formattedEndTime,
                vehicleId: reassignForm.vehicleId
            }

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/changeCarApplication`, params, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                showToast('改派成功', 'success')
                setReassignModalVisible(false)
                // 刷新报表数据
                fetchReports(1)
            } else {
                showToast('改派失败:' + response.data.message, 'error')
            }
        } catch (error) {
            console.error('改派失败:', error)
            showToast('网络错误，请稍后重试', 'error')
        } finally {
            // 设置当前记录的改派状态为false
            setChangeStates(prev => ({
                ...prev,
                [selectedReport.applicationId]: false
            }))
        }
    }

    // 返回首页
    const handleBack = () => {
        navigate('/')
    }

    // 重置筛选条件
    const handleResetFilters = () => {
        setFilters({
            keyword: '',
            applyStartTime: '',
            applyEndTime: ''
        })
        setShowOverdue(false)
        setSelectedCity('')
    }

    // 导出报表
    const handleExport = async () => {
        try {
            // 显示加载提示
            showToast('正在导出，请稍候...', 'info')
            
            // 获取用户信息和token
            const storedUserInfo = localStorage.getItem('userInfo')
            if (!storedUserInfo) {
                showToast('用户信息丢失，请重新登录', 'error')
                return
            }

            const userInfo = JSON.parse(storedUserInfo)

            // 生成自定义文件名
            const timestamp = new Date().getTime()
            const fileName = `用车报表_${timestamp}.xlsx`
            
            console.log('开始导出，文件名:', fileName)

            // 构建请求参数
            const exportParams = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                keyword: filters.keyword,
                applyStartTime: filters.applyStartTime,
                applyEndTime: filters.applyEndTime,
                cityName: selectedCity || undefined, // 归属地筛选
                showOverdue: showOverdue, // 超期未还车筛选：导出时同样只导符合条件的
                _paging: false // 导出全部数据
            }

            console.log('导出请求参数:', exportParams)

            // 发送导出请求
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/export`, exportParams, {
                headers: {
                    'Content-Type': 'application/json'
                },
                responseType: 'blob' // 重要：设置响应类型为blob
            })

            console.log('导出响应:', {
                status: response.status,
                headers: response.headers,
                dataType: typeof response.data,
                dataSize: response.data ? response.data.size : 0
            })

            // 处理响应
            if (response.status === 200 && response.data) {
                const blob = response.data
                
                console.log('导出成功，准备下载:', {
                    blobSize: blob.size,
                    blobType: blob.type
                })

                // 创建下载链接
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', fileName)
                link.style.display = 'none'
                
                // 添加到DOM
                document.body.appendChild(link)
                
                // 触发点击
                link.click()
                
                // 延迟清理
                setTimeout(() => {
                    document.body.removeChild(link)
                    window.URL.revokeObjectURL(url)
                    console.log('下载完成，清理资源')
                }, 100)
                
                showToast('导出成功，文件正在下载', 'success')
            } else {
                console.error('导出失败，响应状态:', response.status)
                showToast('导出失败，请稍后重试', 'error')
            }
        } catch (error) {
            console.error('导出失败:', error)
            showToast(`导出失败: ${error.message || '未知错误'}`, 'error')
        }
    }

    return (
        <div
            className="car-report-page"
            ref={containerRef}
        >
            <h2>用车报表</h2>

            {userLoading ? (
                <div className="loading-indicator">
                    <p>正在检查权限...</p>
                </div>
            ) : (!isAdmin && !isDriver) ? (
                <div className="error-message">
                    <p>您没有权限访问此页面</p>
                    <button className="back-btn" onClick={handleBack}>返回首页</button>
                </div>
            ) : (
                <>
                    {/* 筛选条件 */}
                    <div className="filter-container">
                        <div className="filter-row filter-main">
                            <div className="filter-item search-input">
                                <span>检索条件:</span>
                                <input
                                    type="text"
                                    name="keyword"
                                    value={filters.keyword}
                                    onChange={handleFilterChange}
                                    placeholder="车牌号、申请单号或申请人"
                                />
                            </div>
                            <div className="filter-item time-range">
                                <span>申请时间:</span>
                                <input
                                    type="date"
                                    value={filters.applyStartTime}
                                    onChange={(e) => handleTimeRangeChange('applyStartTime', e.target.value)}
                                />
                                <span className="time-separator">-</span>
                                <input
                                    type="date"
                                    value={filters.applyEndTime}
                                    onChange={(e) => handleTimeRangeChange('applyEndTime', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="filter-row">
                            <div className="filter-item" style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                                <span>超期未还车:</span>
                                <input
                                    type="checkbox"
                                    id="showOverdue"
                                    checked={showOverdue}
                                    onChange={(e) => setShowOverdue(e.target.checked)}
                                    style={{ width: 'auto', height: '20px', cursor: 'pointer' }}
                                />
                            </div>
                            <div className="filter-item">
                                <span>车辆归属地:</span>
                                <select className="city-filter-inline" value={selectedCity} onChange={e=>setSelectedCity(e.target.value)}>
                                    <option value="">全部城市</option>
                                    {cityList.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="filter-row">
                            <div className="filter-actions">
                                <button className="search-btn" onClick={() => fetchReports(1)}>查询</button>
                                <button className="reset-btn" onClick={handleResetFilters}>重置</button>
                                {isAdmin && <button className="export-btn" onClick={handleExport}>导出</button>}
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-indicator">
                            <p>正在加载报表数据...</p>
                        </div>
                    ) : error ? (
                        <div className="error-message">
                            <p>{error}</p>
                            <button className="retry-btn" onClick={() => fetchReports(1)}>重试</button>
                        </div>
                    ) : (
                        <>
                        <div className="reports-list">
                            {reports.length === 0 ? (
                                <div className="no-reports">
                                    <p>暂无报表数据</p>
                                </div>
                            ) : (
                                <>
                                    <div className="report-cards">
                                        {/* 城市筛选已由后端 SQL 过滤（cityName），前端不再本地 filter */}
                                        {reports.map(report => (
                                            <div key={report.applicationId} className="report-card">
                                                <div className="card-header">
                                                    <h3>{report.plateNumber}</h3>
                                                    {report.cityName && <span className="city-tag">{report.cityName}</span>}
                                                    <span className={`status-badge ${getStatusClass(report.approvalStatus || 0)}`}>
                                                        {getStatusText(report.approvalStatus || 0)}
                                                    </span>
                                                </div>
                                                <div className="card-body">
                                                    <div className="card-info">
                                                        <div className="info-item">
                                                            <span className="label">车辆归属地:</span>
                                                            <span className="value">{report.cityName}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">车辆型号:</span>
                                                            <span className="value">{report.modelName}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">申请单号:</span>
                                                            <span className="value">{report.applicationNo}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">申请人:</span>
                                                            <span className="value">{report.applicantName}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">申请人电话:</span>
                                                            <span className="value">{report.applicantPhone}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">所属部门:</span>
                                                            <span className="value">{report.applicantDept}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">驾驶方式:</span>
                                                            <span className="value">{report.driveType === 1 ? '自驾' : '司机代驾'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">申请类型:</span>
                                                            <span className="value">
                                                                {report.isPersonal === 0 ? '本人申请' : '代人申请'}
                                                            </span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">用车人:</span>
                                                            <span className="value">{report.vehicleUserName}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">使用场景:</span>
                                                            <span className="value">
                                                                {report.usageScenarios === 1 ? '员工外出' :
                                                                    report.usageScenarios === 2 ? '一般客户接待' :
                                                                        report.usageScenarios === 3 ? '重要客户接待' :
                                                                            report.usageScenarios}
                                                            </span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">申请用车时间:</span>
                                                            <span className="value">{report.applyTime}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">实际还车时间:</span>
                                                            <span className="value">{report.returnApplyTime || '-'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">预约用车时间:</span>
                                                            <span className="value">{report.expectedStartTime}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">预约还车时间:</span>
                                                            <span className={`value${report.approvalStatus === 1 && new Date().getTime() > (parseLocalDateTime(report.expectedEndTime) || 0).getTime() ? ' overdue' : ''}`}>{report.expectedEndTime}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">出发地:</span>
                                                            <span className="value">{report.expectedLocation}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">目的地:</span>
                                                            <span className="value">{report.destination}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">用车总里程(km):</span>
                                                            <span className="value">{report.totalMileage || '-'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">用车总费用:</span>
                                                            <span className="value">{report.amount || 0} 元</span>
                                                        </div>
                                                        <div className="info-item full-width">
                                                            <span className="label">用车原因:</span>
                                                            <span className="value">{report.reason}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-footer">
                                                    {report.approvalStatus === 1 && isAdmin && (
                                                        <div className="btn-row">
                                                            <button
                                                                className="urge-btn"
                                                                onClick={() => urgeReturnCar(report.applicationId)}
                                                                disabled={urgingStates[report.applicationId]}
                                                            >
                                                                {urgingStates[report.applicationId] ? '催还中...' : '催促还车'}
                                                            </button>
                                                            <button
                                                                className="reassign-btn"
                                                                onClick={() => changeCar(report)}
                                                                disabled={changeStates[report.applicationId]}
                                                            >
                                                                {changeStates[report.applicationId] ? '改派中...' : '改派车辆'}
                                                            </button>
                                                            <button
                                                                className="return-btn"
                                                                onClick={() => returnCar(report.applicationId)}
                                                                disabled={returningStates[report.applicationId]}
                                                            >
                                                                {returningStates[report.applicationId] ? '还车中...' : '代人还车'}
                                                            </button>
                                                            <button
                                                                className="cancel-btn"
                                                                onClick={() => cancelCarApplication(report.applicationId)}
                                                                disabled={!!cancelingStates[report.applicationId]}
                                                            >
                                                                {cancelingStates[report.applicationId] ? '取消中...' : '取消用车'}
                                                            </button>
                                                        </div>
                                                    )}
                                                    <div className="btn-row">
                                                        <button
                                                            className="detail-btn"
                                                            onClick={() => handleReportDetail(report)}
                                                        >
                                                            用车详情
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {loadingMore ? (
                                        <div className="loading-more">
                                            <p>加载中...</p>
                                        </div>
                                    ) : hasMore ? (
                                        <div className="load-more-container">
                                            <button className="load-more-btn" onClick={handleLoadMore}>
                                                加载更多
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="no-more-data">
                                            <p>没有更多数据了</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        </>
                    )}
                    <div className="action-buttons">
                        <button className="back-btn" onClick={handleBack}>返回首页</button>
                    </div>
                </>
            )}

            {/* 详情弹窗 */}
            {detailModalVisible && selectedReport && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '30px',
                        width: '90%',
                        maxWidth: '600px',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{
                            margin: '0 0 20px 0',
                            textAlign: 'center',
                            fontSize: '18px',
                            color: '#333'
                        }}>
                            用车详情_{selectedReport.plateNumber}
                        </h3>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '15px',
                            marginBottom: '20px'
                        }}>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>车辆归属地:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.cityName}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>申请单号:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.applicationNo}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>申请人姓名:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.applicantName}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>申请人电话:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.applicantPhone}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>所属部门:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.applicantDept}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>驾驶方式:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.driveType === 1 ? '自驾' : '司机代驾'}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>申请类型:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.isPersonal === 0 ? '本人申请' : '代人申请'}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>用车人:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.vehicleUserName}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>使用场景:</span>
                                <span style={{
                                    color: '#333'
                                }}>
                                    {selectedReport.usageScenarios === 1 ? '员工外出' :
                                        selectedReport.usageScenarios === 2 ? '一般客户接待' :
                                            selectedReport.usageScenarios === 3 ? '重要客户接待' :
                                                selectedReport.usageScenarios}
                                </span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>申请时间:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.applyTime}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>还车时间:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.returnApplyTime}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>用车时间:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.expectedStartTime} 至 {selectedReport.expectedEndTime}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>出发地:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.expectedLocation}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>目的地:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.destination}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>同行人:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.passengers || '无'}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>用车原因:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.reason}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>用车总里程:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.totalMileage || 0} km</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>用车总费用:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.amount || 0} 元</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>备注:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedReport.remark || '无'}</span>
                            </div>


                            {/* 还车图片 */}
                            {selectedReport.returnImages && typeof selectedReport.returnImages === 'string' && selectedReport.returnImages.trim() !== '' && (
                                <div style={{
                                    backgroundColor: '#f9f9f9',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    marginTop: '10px'
                                }}>
                                    <span style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: 'bold',
                                        color: '#666'
                                    }}>还车图片:</span>
                                    <div style={{
                                        display: 'flex',
                                        gap: '10px',
                                        flexWrap: 'wrap'
                                    }}>
                                        {(() => {
                                            const images = selectedReport.returnImages.split(',').map(img => img.trim()).filter(img => img);

                                            return images.map((imageUrl, index) => (
                                                <div key={index} style={{
                                                    width: '100px',
                                                    height: '100px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer'
                                                }} onClick={() => {
                                                    fileSvc.previewImage(images, imageUrl)
                                                        .then(() => console.log('预览图片成功'))
                                                        .catch(error => console.log('预览图片失败:', error));
                                                }}>
                                                    <img
                                                        src={imageUrl}
                                                        alt={`还车图片 ${index + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* 确认图片 */}
                            {selectedReport.confirmImages && typeof selectedReport.confirmImages === 'string' && selectedReport.confirmImages.trim() !== '' && (
                                <div style={{
                                    backgroundColor: '#f9f9f9',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    marginTop: '10px'
                                }}>
                                    <span style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: 'bold',
                                        color: '#666'
                                    }}>确认图片:</span>
                                    <div style={{
                                        display: 'flex',
                                        gap: '10px',
                                        flexWrap: 'wrap'
                                    }}>
                                        {(() => {
                                            const images = selectedReport.confirmImages.split(',').map(img => img.trim()).filter(img => img);

                                            return images.map((imageUrl, index) => (
                                                <div key={index} style={{
                                                    width: '100px',
                                                    height: '100px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer'
                                                }} onClick={() => {
                                                    fileSvc.previewImage(images, imageUrl)
                                                        .then(() => console.log('预览图片成功'))
                                                        .catch(error => console.log('预览图片失败:', error));
                                                }}>
                                                    <img
                                                        src={imageUrl}
                                                        alt={`确认图片 ${index + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '10px'
                        }}>
                            <button
                                onClick={handleCloseModal}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}
                            >
                                关闭
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 改派弹窗 */}
            {reassignModalVisible && selectedReport && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1100
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '30px',
                        width: '90%',
                        maxWidth: '500px'
                    }}>
                        <h3 style={{
                            margin: '0 0 20px 0',
                            textAlign: 'center',
                            fontSize: '18px',
                            color: '#333'
                        }}>
                            改派车辆
                        </h3>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            marginBottom: '30px'
                        }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}>
                                    开始时间: <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    name="expectedStartTime"
                                    value={reassignForm.expectedStartTime}
                                    onChange={handleReassignFormChange}
                                    onBlur={fetchAvailableVehicles}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}>
                                    结束时间: <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    name="expectedEndTime"
                                    value={reassignForm.expectedEndTime}
                                    onChange={handleReassignFormChange}
                                    onBlur={fetchAvailableVehicles}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}>
                                    车牌号: <span style={{ color: 'red' }}>*</span>
                                </label>
                                <select
                                    name="plateNumber"
                                    value={reassignForm.plateNumber}
                                    onChange={handlePlateNumberChange}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">请选择车牌号</option>
                                    {availableVehicles.map(vehicle => (
                                        <option key={vehicle.vehicleId} value={vehicle.plateNumber}>
                                            {vehicle.plateNumber} ({vehicle.modelName}) {vehicle.cityName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '10px'
                        }}>
                            <button
                                onClick={handleCloseReassignModal}
                                disabled={changeStates[selectedReport.applicationId]}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: changeStates[selectedReport.applicationId] ? 0.6 : 1
                                }}
                            >
                                取消
                            </button>
                            <button
                                onClick={submitReassign}
                                disabled={changeStates[selectedReport.applicationId]}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#1890ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: changeStates[selectedReport.applicationId] ? 0.6 : 1
                                }}
                            >
                                {changeStates[selectedReport.applicationId] ? '提交中...' : '提交改派'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 图片预览弹窗 */}
            {previewImage && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1200
                }} onClick={closeImagePreview}>
                    <img
                        src={previewImage}
                        alt="预览图片"
                        style={{
                            maxWidth: '90%',
                            maxHeight: '90%',
                            objectFit: 'contain'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button
                        onClick={closeImagePreview}
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            background: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            fontSize: '20px',
                            cursor: 'pointer'
                        }}
                    >
                        ×
                    </button>
                </div>
            )}
        </div>
    );
}